import React, { useState } from "react";
import { ExternalLink, Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toolLogoUrl } from "@/data/ai-lab/aiTools";
import { eventsApi } from "@/lib/ai-lab/api";

export function ToolLogo({ tool, className }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className={cn("inline-flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-sm font-semibold", className)}>
        {tool.name.slice(0, 1)}
      </span>
    );
  }
  return (
    <img
      src={toolLogoUrl(tool)}
      alt=""
      width={40}
      height={40}
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn("h-10 w-10 rounded-lg bg-white object-contain p-1 ring-1 ring-border", className)}
    />
  );
}

/**
 * ToolCard — one tool in the directory or on a toolkit page.
 * `useFor` / `whatToType` override the generic copy with page-specific guidance.
 * `compact` renders a single-row version for the Prompt Builder output panel.
 */
export default function ToolCard({ tool, recommended = tool.recommended, useFor, whatToType, compact = false, context }) {
  if (!tool) return null;
  const open = () => eventsApi.track({ type: "open_tool", toolId: tool.id, context });

  if (compact) {
    return (
      <div className="flex items-center gap-3 rounded-lg border p-3">
        <ToolLogo tool={tool} className="h-9 w-9" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{tool.name}</span>
            {recommended && <Badge className="gap-1 bg-pink-500 text-white hover:bg-pink-500"><Star className="h-3 w-3 fill-current" />Recommended</Badge>}
          </div>
          <p className="truncate text-xs text-muted-foreground">{useFor || tool.tagline}</p>
        </div>
        <Button asChild size="sm" variant="outline" onClick={open}>
          <a href={tool.url} target="_blank" rel="noopener noreferrer">Open <ExternalLink className="ml-1 h-3.5 w-3.5" /></a>
        </Button>
      </div>
    );
  }

  return (
    <Card className={cn("flex h-full flex-col", recommended && "border-pink-300 shadow-[0_0_0_1px_rgba(245,158,11,0.35)] dark:border-pink-700")}>
      <CardContent className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start gap-3">
          <ToolLogo tool={tool} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold leading-tight">{tool.name}</h3>
              {recommended && <Badge className="gap-1 bg-pink-500 text-white hover:bg-pink-500"><Star className="h-3 w-3 fill-current" />Recommended</Badge>}
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{tool.pricing}</p>
          </div>
        </div>
        <p className="text-sm font-medium">{useFor || tool.tagline}</p>
        <p className="text-sm text-muted-foreground">{tool.why}</p>
        {whatToType && (
          <div className="rounded-md bg-muted/60 p-3 text-xs">
            <span className="font-semibold">What to type: </span>
            <span className="text-muted-foreground">{whatToType}</span>
          </div>
        )}
        {!whatToType && tool.bestFor?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tool.bestFor.map((b) => <Badge key={b} variant="secondary" className="font-normal">{b}</Badge>)}
          </div>
        )}
        <div className="mt-auto pt-1">
          <Button asChild className="w-full sm:w-auto" onClick={open}>
            <a href={tool.url} target="_blank" rel="noopener noreferrer">Open Tool <ExternalLink className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
